import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MobileShell } from '@/components/layout/MobileShell';
import { DesktopShell } from '@/components/layout/DesktopShell';
import { OnboardingWizard } from '@/components/OnboardingWizard';
import { useSession } from '@/hooks/useSession';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useStore } from '@/store';

const SearchPage       = lazy(() => import('@/routes/search'));
const ResultsPage      = lazy(() => import('@/routes/results'));
const SeatsPage        = lazy(() => import('@/routes/seats'));
const PassengersPage   = lazy(() => import('@/routes/passengers'));
const PaymentPage      = lazy(() => import('@/routes/payment'));
const ConfirmationPage = lazy(() => import('@/routes/confirmation'));
const LoginPage        = lazy(() => import('@/routes/login'));
const RegisterPage     = lazy(() => import('@/routes/register'));
const ProfilePage      = lazy(() => import('@/routes/profile'));
const MyFlightsPage    = lazy(() => import('@/routes/my-flights'));
const FlightPage       = lazy(() => import('@/routes/flight'));
const LandingPage      = lazy(() => import('@/routes/landing'));
const ContactPage      = lazy(() => import('@/routes/contact'));
const AdminPage        = lazy(() => import('@/routes/admin'));

const PAGE_FALLBACK = (
  <div className="flex items-center justify-center py-20 text-muted" role="status" aria-live="polite">
    <div className="animate-spin text-2xl" aria-hidden="true">✈</div>
    <span className="sr-only">Loading…</span>
  </div>
);

function AppShell() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileShell /> : <DesktopShell />;
}

function AppRoutes() {
  const { isSessionLoading } = useSession();
  useAuth();
  const isAuthenticated  = useStore((s) => s.isAuthenticated);
  const wizardCompleted  = useStore((s) => s.wizardCompleted);
  const showWizard       = isAuthenticated && wizardCompleted === false;

  if (isSessionLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-ink text-white">
        <img
          src="https://cdn.prod.website-files.com/69148a80ebab6c41c3621a67/693ba81b001c674edfb758d7_LOGO-PRONETX-Horiz-Black-2-tone-001%202.png"
          alt="Pronetx Airlines"
          className="h-7 w-auto object-contain mb-3 invert brightness-90"
        />
        <p className="text-[9px] font-semibold text-brand-400 tracking-widest uppercase mb-8">Airlines</p>
        <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
    {showWizard && <OnboardingWizard />}
    <Suspense fallback={PAGE_FALLBACK}>
      <Routes>
        {/* Landing page — shown at root for unauthenticated users */}
        <Route path="/welcome"  element={<LandingPage />} />

        {/* Full-screen auth pages */}
        <Route path="/login"    element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Main app shell — mobile or desktop depending on viewport */}
        <Route path="/" element={<AppShell />}>
          {/* Root: authenticated → profile, guest → login */}
          <Route index element={
            <Navigate to={isAuthenticated ? '/profile' : '/welcome'} replace />
          } />
          <Route path="search"             element={<SearchPage />} />
          <Route path="results"            element={<ResultsPage />} />
          <Route path="seats/:flightId"    element={<SeatsPage />} />
          <Route path="passengers"         element={<PassengersPage />} />
          <Route path="payment"            element={<PaymentPage />} />
          <Route path="confirmation"       element={<ConfirmationPage />} />
          <Route path="profile"            element={<ProfilePage />} />
          <Route path="my-flights"                    element={<MyFlightsPage />} />
          <Route path="my-flights/:confirmationCode"  element={<FlightPage />} />
          <Route path="contact"            element={<ContactPage />} />
          <Route path="admin"              element={<AdminPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
